import { darken } from 'polished';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'wouter';
import { uuid } from '../../../../CodesThatsGottaGoSomewhereElse';
import { TeamWithMembersData, UserLiteData } from '../../../../api/ApiTypes';
import { Icon } from '../../../../concepts/resources/Icon';
import { Colors } from '../../../../ui';
import { Badge } from '../../../../ui/Badge';
import { DropButton } from '../../../../ui/DropButton';
import { Shadows } from '../../../../ui/Standards';
import { Tip } from '../../../../ui/Tip';
import { useOnTeamAddUserViaPopup } from '../../../actions/WorkspaceActions';
import { Popover } from '../../../canvas/ui/Popover';
import { Avatar } from '../../../common/Avatar';
import { Copy, Flexer, Title } from '../../../dashboard/elements/LayoutElements';
import { TeamActionMenuButton } from './TeamActionMenu';
import { TeamMemberInfoView } from './TeamMemberInfoView';

const TeamListItemFrame = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    border-radius: .5rem;
    box-shadow: ${Shadows.sm};
    background: white;
    border: solid .0625rem ${Colors.border};

    ${DropButton} {
        max-width: 100%;
    }
`;

const TeamMemberList = styled.div`
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: .5rem;
`;

const Disc = styled.div`
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    border-radius: 50%;
    font-weight: 450;
`;

const TeamMemberDisc = ({ user, team, readonly }: { user: UserLiteData, team: TeamWithMembersData, readonly?: boolean }) => {

    const [open, setOpen] = useState(false);
    const onClose = useCallback(() => setOpen(false), []);

    return <Tip text={user.name} rest={1} disabled={open}>
        <Popover
            content={<TeamMemberInfoView {...{ user, team, readonly, onClose }} />}
            open={open}
            placement="bottom-start"
            inner={{ direction: 'down', offset: '.5rem' }}
            onOpenChange={setOpen}
        >
            <Avatar.ForUser user={user} clickable />
        </Popover>
    </Tip>;
};

const EmptyDisc = styled(Disc)`
    position: relative;
    margin: .125rem;
    border: dashed 2px ${Colors.border};
    border-radius: 50%;
    color: ${Colors.textLightest};
    cursor: pointer;
`;

const AddDisc = styled(EmptyDisc)`
    position: relative;
    border: dashed 2px ${Colors.border};
    border-radius: 50%;
    color: ${Colors.textLightest};
    cursor: pointer;
    

    &:hover {
        border-color: ${darken(.1, Colors.border)};
        color: ${darken(.1, Colors.textLightest)};
    }
    &:active {
        border-color: ${darken(.2, Colors.border)};
        color: ${darken(.2, Colors.textLightest)};
    }
`;

const MoreDisc = styled(Disc)`
    background: ${Colors.border};
    color: ${Colors.text};
`;

const useTeamItemSlots = (team: TeamWithMembersData) => {
    // 20 slots.  If more than 20 members, slice as 19 so we can show a (...) at the end.
    const base = 20;
    return useMemo(() => {
        const total = team.members.length;
        const count = total > base ? (base - 1) : base;
        const first = team.members.slice(0, count);
        const rest = team.members.slice(count);
        const slots = Array.from({ length: count }).map(uuid);
        return { first, rest, slots };
    }, [team]);
};

export const TeamListItem = ({ team, readonly }: { team: TeamWithMembersData, readonly?: boolean }) => {

    const { onTeamAddUser } = useOnTeamAddUserViaPopup(team);
    const { first, rest, slots } = useTeamItemSlots(team);

    return <TeamListItemFrame>
        <Flexer flex="row" align="start" justify="space-between" gap="1">
            <Flexer relative grow height="3">
                <Link to={`/manage/teams/${team.id}`}>
                    <Flexer absolute inset>
                        <DropButton overflow=".5" style={{ justifyContent: 'start' }}>
                            <Flexer maxWidth="100%" flex="column" gap=".25">
                                <Title size="lg" weight="400" flush truncate style={{ paddingBottom: '.25rem' }}>{team.label}</Title>
                                <Copy flush color={Colors.textLight}>
                                    {team.members.length} members
                                    &nbsp;
                                    {!!team.invites.length && <span>(+{team.invites.length} pending)</span>}
                                </Copy>
                            </Flexer>
                        </DropButton>
                    </Flexer>
                </Link>
            </Flexer>
            {!readonly && <TeamActionMenuButton {...{ team }} />}
            {!!readonly && <Flexer>
                <Badge color={Colors.gray100}>
                    {team.id}
                </Badge>
            </Flexer>}
        </Flexer>
        <TeamMemberList>
            {slots.map((x, i) =>
                !!first[i] ? (
                    <TeamMemberDisc key={x} user={first[i]} team={team} readonly={readonly} />
                ) : (
                    readonly ? (
                        <EmptyDisc key={x} />
                    ) : (
                        <AddDisc key={x} onClick={onTeamAddUser}>
                            <Icon size="1.25" src="deqo://icons/hero/plus" />
                        </AddDisc>
                    )
                )
            )}
            {!!rest.length &&
                <MoreDisc>
                    +{rest.length}
                </MoreDisc>
            }
        </TeamMemberList>
    </TeamListItemFrame>;
};
